/**
 * Runtime configuration — env-specific values read from `config.js`
 * (`window.__ESCLOUD_CONFIG__`), which tools/deploy-client.sh writes into
 * dist/ at deploy time. The build itself is env-agnostic (ESCLOUD-248): one
 * `client:<sha>` artifact can be promoted to any environment.
 *
 * Local dev, vitest and Playwright never have an injected config and fall
 * back to the Vite env vars from apps/client/.env.
 */

const injected = typeof window !== 'undefined' ? window.__ESCLOUD_CONFIG__ : undefined;
const cfg = injected ?? {};

export const RUNTIME_CONFIG = {
    auth0Domain: cfg.auth0Domain ?? import.meta.env.VITE_AUTH0_DOMAIN,
    auth0ClientId: cfg.auth0ClientId ?? import.meta.env.VITE_AUTH0_CLIENT_ID,
    auth0Audience: cfg.auth0Audience ?? import.meta.env.VITE_AUTH0_AUDIENCE,
    defaultRegion: cfg.defaultRegion ?? import.meta.env.VITE_DEFAULT_REGION ?? 'eu',
    // Date of manufacture (UDI AI (11), ESCLOUD-76). Deploys inject it; dev
    // and tests fall back to the current month, deterministic enough for a
    // throwaway build and never shipped (production injection is mandatory).
    releaseDate: cfg.releaseDate ?? new Date().toISOString().slice(0, 7),
    // FDA 510(k) clearance number — deploy-injected only; empty (pending) in
    // dev and tests, which keeps unit tests deterministic regardless of env.
    fda510kNumber: cfg.fda510kNumber ?? '',
    // Stripe publishable key (ESCLOUD-282) — publishable by definition, but
    // still deploy-injected so one env-agnostic build serves every environment
    // (each environment has its own Stripe account). Empty means "Stripe not
    // wired here yet"; the checkout screen (ESCLOUD-283) treats that as a
    // failure, not as free.
    stripePublishableKey: cfg.stripePublishableKey ?? import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
} as const;

// Light shape validation only — loud console errors so a bad injection is
// visible in the browser. The hard gate lives in tools/deploy-client.sh,
// which refuses to deploy malformed values in the first place.
//
// An absent config.js is NORMAL outside deployed environments (dev, vitest
// and Playwright run on the Vite env fallback), so an incomplete Auth0
// binding is only an error when a config WAS injected, or when a production
// bundle ends up with no binding at all — never routine dev/test noise.
const auth0Incomplete =
    !RUNTIME_CONFIG.auth0Domain || !RUNTIME_CONFIG.auth0ClientId || !RUNTIME_CONFIG.auth0Audience;
if (auth0Incomplete && (injected !== undefined || import.meta.env.PROD)) {
    console.error(
        injected !== undefined
            ? 'Runtime config: injected config.js has an incomplete Auth0 binding.'
            : 'Runtime config: production bundle without config.js and no VITE_AUTH0_* fallback.'
    );
}
if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(RUNTIME_CONFIG.releaseDate)) {
    console.error(`Runtime config: releaseDate must be YYYY-MM, got "${RUNTIME_CONFIG.releaseDate}"`);
}
if (RUNTIME_CONFIG.fda510kNumber && !/^K\d{6}$/.test(RUNTIME_CONFIG.fda510kNumber)) {
    console.error(
        `Runtime config: fda510kNumber must match K followed by 6 digits, got "${RUNTIME_CONFIG.fda510kNumber}"`
    );
}
if (RUNTIME_CONFIG.stripePublishableKey && !/^pk_(test|live)_/.test(RUNTIME_CONFIG.stripePublishableKey)) {
    // Loud, because the likeliest wrong value is a SECRET key pasted into the
    // wrong variable — which this page must never carry. The hard gate is in
    // tools/deploy-client.sh; this catches a hand-edited local .env too.
    console.error('Runtime config: stripePublishableKey must start with pk_test_ or pk_live_.');
}
